/// The single source of truth for ingredient units of measure.
///
/// Every unit the app recognizes lives here exactly once, classified by *kind*
/// so callers can tell an exact conversion from a bartender's approximation
/// from something that can't be converted at all:
///
///   volume  -- an exact millilitre factor (oz, cl, tsp, ...). Convertible.
///   approx  -- a real pour with only a conventional millilitre value
///              (dash, splash, barspoon). `ml` is nominal: fine for costing,
///              never precise enough to render as a converted figure.
///   count   -- whole things (twist, leaf, wedge). No millilitre value exists.
///   ratio   -- proportional builds ("2 parts gin"). Meaningless without the
///              other parts, so no millilitre value.
///   weight  -- mass, not volume. No millilitre value without a density.
///
/// An unrecognized token resolves to `undefined`. Callers must preserve it
/// verbatim rather than guessing a unit -- guessing is what produced the
/// "2 dashes stored as 59 ml" class of bug this module replaces.
export type UnitKind = 'volume' | 'approx' | 'count' | 'ratio' | 'weight';

export interface UnitDef {
  canonical: string;
  kind: UnitKind;
  /// Millilitres per 1 of this unit. Exact for `volume`, nominal for `approx`,
  /// absent for every other kind (and for `top`/`fill`, which mean "top up"
  /// and have no fixed size at all).
  ml?: number;
  aliases: string[];
}

/// What a bare number with no unit means ("2" -> "2 oz"). Matches the UI's
/// own placeholder (messages/en.json `ingredient-quantity-placeholder`).
/// This is a documented house convention, not a fallback: an *unrecognized*
/// unit must never be coerced to this.
export const DEFAULT_UNIT = 'oz';

export const UNITS: readonly UnitDef[] = [
  // --- exact volumes -------------------------------------------------------
  {
    canonical: 'ml',
    kind: 'volume',
    ml: 1,
    aliases: ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres']
  },
  {
    canonical: 'cl',
    kind: 'volume',
    ml: 10,
    aliases: ['cl', 'centiliter', 'centiliters', 'centilitre', 'centilitres']
  },
  {
    canonical: 'dl',
    kind: 'volume',
    ml: 100,
    aliases: ['dl', 'deciliter', 'deciliters', 'decilitre', 'decilitres']
  },
  {
    canonical: 'l',
    kind: 'volume',
    ml: 1000,
    aliases: ['l', 'liter', 'liters', 'litre', 'litres']
  },
  {
    canonical: 'cc',
    kind: 'volume',
    ml: 1,
    aliases: ['cc', 'cubic centimeter', 'cubic centimeters']
  },
  // `oz` is always FLUID ounces here -- this is a bar app. Weight ounces
  // would need their own token and are not in the source data.
  {
    canonical: 'oz',
    kind: 'volume',
    ml: 29.5735,
    aliases: ['oz', 'ounce', 'ounces', 'oez']
  },
  {
    canonical: 'tsp',
    kind: 'volume',
    ml: 4.92892,
    aliases: ['tsp', 'teaspoon', 'teaspoons']
  },
  {
    canonical: 'tbsp',
    kind: 'volume',
    ml: 14.7868,
    aliases: ['tbsp', 'tblsp', 'tablespoon', 'tablespoons']
  },
  { canonical: 'cup', kind: 'volume', ml: 240, aliases: ['cup', 'cups'] },
  {
    canonical: 'pint',
    kind: 'volume',
    ml: 473.176,
    aliases: ['pint', 'pints', 'pt']
  },
  {
    canonical: 'quart',
    kind: 'volume',
    ml: 946.353,
    aliases: ['quart', 'quarts', 'qt']
  },
  {
    canonical: 'gallon',
    kind: 'volume',
    ml: 3785.41,
    aliases: ['gallon', 'gallons', 'gal']
  },

  // --- approximate pours ---------------------------------------------------
  // `ml` values below are the conventional bar-reference figures. They exist
  // so a future cost/yield calculation has *something* defensible to use;
  // they are not precise and must not be shown to a user as a conversion.
  { canonical: 'dash', kind: 'approx', ml: 0.92, aliases: ['dash', 'dashes'] },
  {
    canonical: 'drop',
    kind: 'approx',
    ml: 0.05,
    aliases: ['drop', 'drops', 'microdrop', 'microdrops']
  },
  {
    canonical: 'bsp',
    kind: 'approx',
    ml: 5,
    aliases: ['bsp', 'barspoon', 'barspoons', 'bar spoon', 'spoon', 'spoons']
  },
  {
    canonical: 'splash',
    kind: 'approx',
    ml: 7.4,
    aliases: ['splash', 'splashes']
  },
  {
    canonical: 'pinch',
    kind: 'approx',
    ml: 0.31,
    aliases: ['pinch', 'pinches']
  },
  { canonical: 'rinse', kind: 'approx', ml: 5, aliases: ['rinse', 'rinses'] },
  { canonical: 'float', kind: 'approx', ml: 15, aliases: ['float', 'floats'] },
  {
    canonical: 'shot',
    kind: 'approx',
    ml: 44.36,
    aliases: ['shot', 'shots', 'jigger', 'jiggers']
  },
  {
    canonical: 'tincture',
    kind: 'approx',
    ml: 0.92,
    aliases: ['tincture', 'tinctures']
  },
  // "top with soda" / "fill with tonic" -- a real instruction with no size.
  { canonical: 'top', kind: 'approx', aliases: ['top', 'tops'] },
  { canonical: 'fill', kind: 'approx', aliases: ['fill', 'fills'] },

  // --- proportional --------------------------------------------------------
  {
    canonical: 'part',
    kind: 'ratio',
    aliases: ['part', 'parts', 'measure', 'measures']
  },

  // --- whole things --------------------------------------------------------
  { canonical: 'twist', kind: 'count', aliases: ['twist', 'twists'] },
  { canonical: 'leaf', kind: 'count', aliases: ['leaf', 'leaves'] },
  { canonical: 'sprig', kind: 'count', aliases: ['sprig', 'sprigs', 'spg'] },
  { canonical: 'wheel', kind: 'count', aliases: ['wheel', 'wheels'] },
  { canonical: 'wedge', kind: 'count', aliases: ['wedge', 'wedges'] },
  { canonical: 'slice', kind: 'count', aliases: ['slice', 'slices', 'sliced'] },
  { canonical: 'piece', kind: 'count', aliases: ['piece', 'pieces', 'pc'] },
  { canonical: 'cube', kind: 'count', aliases: ['cube', 'cubes'] },
  { canonical: 'stick', kind: 'count', aliases: ['stick', 'sticks'] },
  { canonical: 'can', kind: 'count', aliases: ['can', 'cans'] },
  {
    canonical: 'bottle',
    kind: 'count',
    aliases: ['bottle', 'bottles', 'fifth']
  },
  { canonical: 'scoop', kind: 'count', aliases: ['scoop', 'scoops'] },
  { canonical: 'cherry', kind: 'count', aliases: ['cherry', 'cherries'] },
  { canonical: 'egg', kind: 'count', aliases: ['egg', 'eggs'] },
  { canonical: 'berry', kind: 'count', aliases: ['berry', 'berries'] },
  { canonical: 'spear', kind: 'count', aliases: ['spear', 'spears'] },
  { canonical: 'coin', kind: 'count', aliases: ['coin', 'coins'] },
  { canonical: 'strip', kind: 'count', aliases: ['strip', 'strips'] },
  { canonical: 'peel', kind: 'count', aliases: ['peel', 'peels'] },
  {
    canonical: 'disc',
    kind: 'count',
    aliases: ['disc', 'discs', 'disk', 'disks']
  },
  { canonical: 'ribbon', kind: 'count', aliases: ['ribbon', 'ribbons'] },
  { canonical: 'fan', kind: 'count', aliases: ['fan', 'fans'] },
  { canonical: 'crescent', kind: 'count', aliases: ['crescent', 'crescents'] },
  { canonical: 'bouquet', kind: 'count', aliases: ['bouquet', 'bouquets'] },
  { canonical: 'clove', kind: 'count', aliases: ['clove', 'cloves'] },
  { canonical: 'pod', kind: 'count', aliases: ['pod', 'pods'] },
  { canonical: 'chunk', kind: 'count', aliases: ['chunk', 'chunks'] },
  { canonical: 'handful', kind: 'count', aliases: ['handful', 'handfuls'] },
  {
    canonical: 'package',
    kind: 'count',
    aliases: ['package', 'packages', 'pkg', 'bag', 'bags', 'jar', 'jars']
  },
  { canonical: 'whole', kind: 'count', aliases: ['whole'] },

  // --- weight --------------------------------------------------------------
  { canonical: 'g', kind: 'weight', aliases: ['g', 'gram', 'grams', 'gr'] },
  { canonical: 'kg', kind: 'weight', aliases: ['kg', 'kilogram', 'kilograms'] },
  { canonical: 'lb', kind: 'weight', aliases: ['lb', 'lbs', 'pound', 'pounds'] }
];

const BY_ALIAS = new Map<string, UnitDef>();
for (const def of UNITS) {
  for (const alias of def.aliases) {
    BY_ALIAS.set(alias, def);
  }
  // Every canonical name must resolve to itself even if it was left out of
  // the alias list.
  BY_ALIAS.set(def.canonical, def);
}

/// Looks up a unit token (any spelling, any case). Returns `undefined` for
/// anything not in the registry -- callers must NOT substitute a default.
export function resolveUnit(
  token: string | undefined | null
): UnitDef | undefined {
  if (!token) {
    return undefined;
  }
  return BY_ALIAS.get(token.trim().toLowerCase());
}

/// The canonical spelling of a unit token, or `undefined` if unrecognized.
export function canonicalUnit(
  token: string | undefined | null
): string | undefined {
  return resolveUnit(token)?.canonical;
}

/// Converts an amount to millilitres. Returns `undefined` when no honest
/// conversion exists (count/ratio/weight units, `top`/`fill`, and anything
/// unrecognized). Check `isExactVolume()` before presenting the result as
/// anything other than an estimate.
export function toMilliliters(
  amount: number,
  unit: string | undefined | null
): number | undefined {
  const def = resolveUnit(unit);
  if (!def || def.ml === undefined || !Number.isFinite(amount)) {
    return undefined;
  }
  return amount * def.ml;
}

/// True only for units whose millilitre factor is exact, i.e. safe to convert
/// and display. `dash` and friends are deliberately excluded.
export function isExactVolume(unit: string | undefined | null): boolean {
  return resolveUnit(unit)?.kind === 'volume';
}

/// Every exact millilitre factor, largest first. Used by the data-repair pass
/// to recover a lost unit from the ratio between a stored millilitre value and
/// its display amount.
export const EXACT_VOLUME_FACTORS: readonly {
  canonical: string;
  ml: number;
}[] = UNITS.filter(u => u.kind === 'volume' && u.ml !== undefined)
  .map(u => ({ canonical: u.canonical, ml: u.ml as number }))
  .sort((a, b) => b.ml - a.ml);
